export interface PipelineRow {
  id: string;
  platform: string;
  status: string;
  isAutomatic: boolean;
  appVersion: string;
  appBuildNumber: string;
  startedAt: string;
  completedAt: string | null;
  circleciPipelineId: string;
  submitOutcome: string;
  submitOutcomeDetail: string;
  includeReleaseNotes: boolean;
  triggerReason: unknown;
}

/**
 * A successful build normally leaves `pending` within a few minutes, but the
 * post-build task retries for ~21 minutes before giving up. Past that window a
 * still-pending build means the task was lost, so staff can drive the submit
 * (which syncs metadata first) themselves.
 */
const STUCK_PENDING_MINUTES = 30;

export type PipelineSubmitState = "ready" | "retry" | "stuck";

const minutesSince = (timestamp: string | null): number | null => {
  if (!timestamp) return null;
  const parsed = new Date(timestamp).getTime();
  if (Number.isNaN(parsed)) return null;
  return (Date.now() - parsed) / 60000;
};

export const getPipelineSubmitState = (
  row: PipelineRow
): PipelineSubmitState | null => {
  if (
    (row.platform !== "ios" && row.platform !== "android") ||
    row.status !== "success"
  ) {
    return null;
  }

  switch (row.submitOutcome) {
    case "ready_to_submit":
      return "ready";
    case "failed":
    case "skipped_never_published":
      return "retry";
    case "pending": {
      const age = minutesSince(row.completedAt ?? row.startedAt);
      return age !== null && age >= STUCK_PENDING_MINUTES ? "stuck" : null;
    }
    default:
      return null;
  }
};

export const canSubmitPipeline = (row: PipelineRow): boolean =>
  getPipelineSubmitState(row) !== null;

/**
 * Newest actionable pipeline per platform for ready-to-submit cards.
 * Pipelines are expected newest-first; keeps the first hit for each platform
 * so an actionable Android build cannot hide a pending iOS submit (and vice versa).
 */
export const getActionablePipelines = (
  pipelines: PipelineRow[]
): PipelineRow[] => {
  const seenPlatforms = new Set<string>();
  const actionable: PipelineRow[] = [];
  for (const pipeline of pipelines) {
    if (!canSubmitPipeline(pipeline) || seenPlatforms.has(pipeline.platform)) {
      continue;
    }
    seenPlatforms.add(pipeline.platform);
    actionable.push(pipeline);
  }
  return actionable;
};

/** Newest actionable pipeline across platforms (single-card callers). */
export const getActionablePipeline = (
  pipelines: PipelineRow[]
): PipelineRow | null => getActionablePipelines(pipelines)[0] ?? null;

/** @deprecated Prefer getActionablePipelines */
export const getActionableIosPipeline = getActionablePipeline;
